import { useContext, useEffect } from "react";
import { Typography, Icon, Button, Link, Box } from "@mui/material";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { AuthContext } from "src/utils/AuthContext.js";
import { UpgradeConsumerButton } from "../../upgrades/UpgradeConsumerButton.js";
import { getPageRoute, getApiRoute } from "src/services/Config.js";
import InstructionsLayout from "../../instructions/InstructionsLayout.js";
import { Helmet } from "react-helmet";
import axios from "axios";
import { LoadingButton } from "src/components/blocks/LoadingButton";
import { useMutation } from "@tanstack/react-query";
import { useSnackbarContext } from "src/utils/SnackbarContext";
import { customEvent } from "src/utils/gtag.js";

const StanzaPlusCalendar = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { handle, calendarId } = useParams();
  const forwardUrl = searchParams.get('forward') || getPageRoute("home", "HOME");

  const { 
    setSnackbarOpen, 
    setSnackbarMessage, 
    setSnackbarSeverity,
    showSnackBar 
  } = useSnackbarContext();

  // Download the sample calendar
  const handleDownloadSample = async () => {
    if (!handle || !calendarId) {
      throw new Error('Missing handle or calendarId');
    }

    const response = await axios.get(getApiRoute("calendar", "GET_CALENDAR_SAMPLE", { handle, calendarId }), {
      responseType: 'blob'
    });
    
    // Create a URL for the blob
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', `${handle}-sample.ics`);
    
    // Append to the document and trigger the click
    document.body.appendChild(link);
    link.click();

    // Clean up
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  // Add page load tracking
  useEffect(() => {
    if (user) {
      customEvent({
        name: `upgrade_trigger_stanza_plus_calendar`,
        category: "Upgrade",
        label: "Landed on Stanza+ Calendar Page",
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          calendar_id: calendarId,
          calendar_handle: handle
        },
      });
    }
  }, [Boolean(user), calendarId, handle]);

  const { mutate: downloadSample, isLoading: isDownloading } = useMutation({
    mutationFn: async () => {
      // Track download attempt
      customEvent({
        name: "user_click_download_sample",
        category: "Upgrade",
        label: "Sample Download Attempt",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendarId,
          calendar_handle: handle
        },
      });

      return handleDownloadSample();
    },
    onSuccess: () => {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "Sample calendar downloaded successfully. You can now open it in your calendar app.",
        "success"
      );
    },
    onError: (error) => {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        error.message || "Failed to download sample calendar",
        "error"
      );
    }
  });

  useEffect(() => {
    if (user?.hasValidSubscription) {
      // If user already has a subscription, redirect them back to where they came from
      navigate(forwardUrl);
    }

    // Scroll to top on load
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, [user, navigate, forwardUrl]);

  return (
    <>
      <Helmet>
        <title>Stanza+ Exclusive Calendar</title>
        <meta
          name="description"
          content="This premium calendar is exclusive to Stanza+ members. Upgrade today to access this and other premium calendars."
        />
      </Helmet>
      
      <InstructionsLayout>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
          margin: '80px auto 0',
        }}>
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              display: "block",
              margin: "0",
              marginLeft: "0",
              marginRight: "auto",
              color: `${theme.palette.icon.primary}`,
              verticalAlign: "middle",
              fontSize: "2rem",
              mb: 1,
              fontWeight: 300,
              textAlign: "left",
              width: "100%",
            }}
          >
            lock
          </Icon>
          
          <Typography variant="h3" sx={{ mb: 1, textAlign: "left" }}>
            This calendar is only available to <b>Stanza+ members.</b>
          </Typography>
          
          <UpgradeConsumerButton 
            sx={{ 
              mt: 3.5, 
              width: "100%",
              py: 1.5,
              borderRadius: 50,
              fontWeight: 500,
              textTransform: "none"
            }}
            forwardUrl={forwardUrl}
          >
            BECOME A MEMBER TODAY
          </UpgradeConsumerButton>
          
          <LoadingButton
            size="grown"
            variant="outlined"
            onClick={() => downloadSample()}
            loading={isDownloading}
            sx={{ 
              mt: 2, 
              mb: 2, 
              height: "auto", 
              width: "100%",
              py: 1.5,
              borderRadius: 50,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            DOWNLOAD A SAMPLE
          </LoadingButton>
          
          <Link
            href="https://spotlight.stanzacal.com/what-makes-a-calendar-exclusive-on-stanza/"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ 
              textDecoration: "underline",
              color: "text.secondary",
              mt: 2,
              mb: 4
            }}
          >
            Learn more
          </Link>
        </Box>
      </InstructionsLayout>
    </>
  );
};

export default StanzaPlusCalendar;